/**
 * The timeline board's geometry — the day window and the lane layout, as pure functions.
 *
 * ⭐ Separated from the component on purpose. `apps/web`'s vitest runs `environment: 'node'`
 * and this repo's own rule is that rendering is proved in a real browser — so the arithmetic
 * that decides WHERE a card sits is the half a node test can actually hold to account. What
 * is left in the component is markup.
 */

/** Default window, per 113/A2: 7am–6pm. Minutes since midnight at the SHOP. */
export const DEFAULT_WINDOW_START = 7 * 60;
export const DEFAULT_WINDOW_END = 18 * 60;

export interface Span {
  /** Minutes since midnight at the shop. */
  start: number;
  /**
   * Minutes since midnight at the shop, or null for an open-ended visit.
   *
   * ⚠️ Null is common and is NOT zero. `scheduledEndAt` is nullable and neither creation
   * sheet sets it, so most real visits arrive here open-ended.
   */
  end: number | null;
}

/**
 * The hours the board draws: 7am–6pm, WIDENED to contain everything booked (113/A2).
 *
 * ⚠️ It widens on the UNCLAMPED values — a 6am start pulls the window back to 6am rather
 * than being drawn at the left edge as though it were 7am. A visit the board cannot show is
 * the same class of defect as the Unassigned lane nothing could clear: work that exists and
 * is invisible.
 *
 * ⚠️ An open-ended visit widens on its START only. Inventing an end to widen by would be a
 * guess about how long the job runs, and the guess would move the whole grid.
 */
export function dayWindow(spans: Span[]): { start: number; end: number } {
  let start = DEFAULT_WINDOW_START;
  let end = DEFAULT_WINDOW_END;
  for (const s of spans) {
    if (Number.isFinite(s.start)) start = Math.min(start, s.start);
    // The end of a visit widens the window; a NULL end contributes only its start, below.
    if (s.end !== null && Number.isFinite(s.end)) end = Math.max(end, s.end);
    /**
     * ⚠️ A late start widens the window PAST itself, not merely up to itself. Extending only
     * to `s.start` produced a window that ended at the exact minute a 7pm visit began — the
     * card had zero room and vanished, which is the same "work that exists and is invisible"
     * failure the widening exists to prevent. `OPEN_ENDED_DRAW_MINUTES` is the drawing
     * minimum, not a claimed duration; the ceil below rounds it out to the hour anyway.
     */
    if (Number.isFinite(s.start)) {
      end = Math.max(end, s.start + OPEN_ENDED_DRAW_MINUTES);
    }
  }
  // Whole hours, so the column headers are clock hours and not "6:43".
  start = Math.floor(start / 60) * 60;
  end = Math.ceil(end / 60) * 60;
  // A window must have width even if the day holds a single instant.
  if (end <= start) end = start + 60;
  return { start, end };
}

/** The hour marks a window draws, e.g. [420, 480, …] for 7am onward. */
export function hoursOf(win: { start: number; end: number }): number[] {
  const out: number[] = [];
  for (let m = win.start; m < win.end; m += 60) out.push(m);
  return out;
}

/** Before 8am or after 5pm reads as unusual without being forbidden (113 decision 10). */
export const OFF_HOURS_START = 8 * 60;
export const OFF_HOURS_END = 17 * 60;
export const isOffHour = (minute: number) =>
  minute < OFF_HOURS_START || minute >= OFF_HOURS_END;

/**
 * How long a card is drawn for when the visit has no end time.
 *
 * ⚠️ This is a DRAWING minimum, never a duration. It is not written anywhere, not summed
 * into booked hours, and not sent back to the server — an open-ended visit that rendered as
 * a hairline would be unclickable, and one drawn as an hour would be a claim the shop never
 * made. The card says "no end time" in its own words; this only gives it a box.
 */
export const OPEN_ENDED_DRAW_MINUTES = 30;

export interface Placed<T> {
  item: T;
  /** 0–1 across the window. */
  left: number;
  width: number;
  /** Which sub-track within the lane, and how many tracks the lane needs. */
  track: number;
  tracks: number;
}

/**
 * Lay one lane's visits out, putting OVERLAPPING visits side by side (113/A6).
 *
 * ⭐ Side by side, not stacked in sub-rows: lanes stay one row tall so a shop with eight
 * rigs fits on a screen, and — under the tech-keyed lane — an overlap means one person is
 * double-booked, which is a scheduling error worth drawing as an obvious collision rather
 * than hiding in a second row that reads like a normal second job.
 *
 * The algorithm is the standard calendar sweep: walk in start order, put each visit in the
 * first track whose last visit has already ended, and let the CLUSTER share the height. Only
 * visits that actually overlap each other split the lane — a lane with one 9am and one 3pm
 * job draws both full height.
 */
export function layoutLane<T extends Span>(
  items: T[],
  win: { start: number; end: number },
): Placed<T>[] {
  const span = Math.max(1, win.end - win.start);
  const drawnEnd = (s: Span) =>
    s.end !== null && s.end > s.start ? s.end : s.start + OPEN_ENDED_DRAW_MINUTES;

  // Start order, with the longer visit first on a tie so the wide one takes track 0 and the
  // layout is stable between renders rather than depending on input order.
  const sorted = [...items].sort(
    (a, b) => a.start - b.start || drawnEnd(b) - drawnEnd(a),
  );

  /** Contiguous groups of mutually-overlapping visits — each shares a height. */
  const out: Placed<T>[] = [];
  let cluster: T[] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (cluster.length === 0) return;
    const trackEnds: number[] = [];
    const placed: { item: T; track: number }[] = [];
    for (const it of cluster) {
      let track = trackEnds.findIndex((e) => e <= it.start);
      if (track === -1) {
        track = trackEnds.length;
        trackEnds.push(0);
      }
      trackEnds[track] = drawnEnd(it);
      placed.push({ item: it, track });
    }
    for (const p of placed) {
      const s = Math.max(win.start, Math.min(p.item.start, win.end - 1));
      const e = Math.min(win.end, Math.max(drawnEnd(p.item), s + 1));
      /**
       * ⚠️ A floor on the drawn width, and it is not cosmetic: a zero-width card is on the
       * board and unreachable — it cannot be clicked, dragged, or tabbed to, so the visit is
       * present and unusable. `dayWindow` normally guarantees room, but this function is
       * callable with any window and must not produce an invisible target for one.
       */
      const width = Math.max((e - s) / span, 2 / span);
      out.push({
        item: p.item,
        left: Math.min((s - win.start) / span, 1 - width),
        width,
        track: p.track,
        tracks: trackEnds.length,
      });
    }
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const it of sorted) {
    if (cluster.length > 0 && it.start >= clusterEnd) flush();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, drawnEnd(it));
  }
  flush();
  return out;
}

/**
 * Where a drop landed, in minutes since midnight at the shop, snapped to a grid.
 *
 * ⚠️ Snapped to 15 minutes, and CLAMPED to the window. A drop two pixels past the right edge
 * would otherwise book a visit at 6:02pm on a board whose last column is 5pm, and the visit
 * would then widen the window on the next render — the board rearranging itself because of
 * where a pointer stopped.
 */
export const SNAP_MINUTES = 15;
export function dropMinute(
  fraction: number,
  win: { start: number; end: number },
): number {
  const raw = win.start + fraction * (win.end - win.start);
  const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES;
  return Math.min(win.end - SNAP_MINUTES, Math.max(win.start, snapped));
}
